import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  LinearProgress,
  Fab,
  Dialog,
  Tabs,
  Tab,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add,
  PlaylistPlay,
  Public,
  Lock,
  Edit,
  Visibility,
  Delete
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { generateClient } from 'aws-amplify/api';
import { listUserWatchlists } from '../../graphql/queries';
import { createUserWatchlist, deleteUserWatchlist } from '../../graphql/mutations';
import WatchlistManager from './WatchlistManager';
import WatchlistViewer from './WatchlistViewer';
import WatchlistSelector from './WatchlistSelector';
import { enrichWatchlistsWithCounts, calculateTotalItems } from '../../utils/watchlistCountUtils';

const WatchlistDashboard = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const theme = useTheme();
  const [watchlists, setWatchlists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedWatchlist, setSelectedWatchlist] = useState(null);
  const [showManager, setShowManager] = useState(false);
  const [showViewer, setShowViewer] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState(null);
  const [cleanupInProgress, setCleanupInProgress] = useState(false);

  const [hasLoaded, setHasLoaded] = useState(false);
  
  // Load watchlists when user is available
  useEffect(() => {
    if (user && !hasLoaded && !loading) {
      loadWatchlists();
    }
  }, [user, hasLoaded, loading]);

  const loadWatchlists = useCallback(async () => {
    if (loading || !user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const client = generateClient();
      
      const result = await client.graphql({
        query: listUserWatchlists,
        variables: {
          filter: { userID: { eq: user.id } },
          limit: 100
        }
      });
      
      const userWatchlists = result.data?.listUserWatchlists?.items || [];
      
      // Check for duplicate default watchlists and clean them up
      const defaultWatchlists = userWatchlists.filter(w => w.isDefault === true);
      let cleanedWatchlists = [...userWatchlists];
      
      if (defaultWatchlists.length > 1) {
        console.log(`Found ${defaultWatchlists.length} default watchlists, cleaning up duplicates...`);
        setCleanupInProgress(true);
        
        try {
          // Keep the oldest default watchlist and delete the rest
          const sortedDefaults = defaultWatchlists.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
          const toKeep = sortedDefaults[0];
          const toDelete = sortedDefaults.slice(1);
          
          console.log(`Keeping watchlist ${toKeep.id}, deleting ${toDelete.length} duplicates`);
          
          // Delete duplicate defaults (but don't fail if some deletions fail)
          const deletePromises = toDelete.map(async (duplicate) => {
            try {
              const DELETE_WATCHLIST_SIMPLE = `
                mutation DeleteUserWatchlist($input: DeleteUserWatchlistInput!) {
                  deleteUserWatchlist(input: $input) {
                    id
                  }
                }
              `;
              
              const deleteResult = await client.graphql({
                query: DELETE_WATCHLIST_SIMPLE,
                variables: {
                  input: { id: duplicate.id }
                }
              });
              
              if (deleteResult.errors && deleteResult.errors.length > 0) {
                console.error(`GraphQL errors deleting duplicate watchlist ${duplicate.id}:`, deleteResult.errors);
                return false; // Deletion failed
              }
              
              return true; // Deletion succeeded
            } catch (deleteError) {
              console.error(`Error deleting duplicate watchlist ${duplicate.id}:`, deleteError);
              return false; // Deletion failed
            }
          });
          
          const deleteResults = await Promise.allSettled(deletePromises);
          const successfulDeletions = deleteResults.filter(result => 
            result.status === 'fulfilled' && result.value === true
          ).length;
          
          console.log(`Successfully deleted ${successfulDeletions} of ${toDelete.length} duplicate watchlists`);
          
          // Remove successfully deleted watchlists from the array
          // For safety, just keep the one we decided to keep
          cleanedWatchlists = userWatchlists.filter(w => 
            !w.isDefault || w.id === toKeep.id
          );
        } catch (cleanupError) {
          console.error('Error during watchlist cleanup:', cleanupError);
          // If cleanup fails, just use the original list
          cleanedWatchlists = userWatchlists;
        } finally {
          setCleanupInProgress(false);
        }
      } else if (defaultWatchlists.length === 0) {
        // Create default watchlist if none exists
        try {
          const defaultWatchlist = await createDefaultWatchlist();
          if (defaultWatchlist) {
            cleanedWatchlists.push(defaultWatchlist);
          }
        } catch (createError) {
          console.error('Failed to create default watchlist:', createError);
          // Continue without default watchlist - user can create manually
        }
      }
      
      // Enrich watchlists with actual item counts
      const watchlistsWithCorrectCounts = await enrichWatchlistsWithCounts(cleanedWatchlists);
      
      setWatchlists(watchlistsWithCorrectCounts);
      setHasLoaded(true);
    } catch (error) {
      console.error('Error loading watchlists:', error);
      setError('Failed to load watchlists. Please try again.');
      addNotification({ type: 'error', message: 'Failed to load watchlists' });
    } finally {
      setLoading(false);
    }
  }, [user, loading, addNotification]);

  const createDefaultWatchlist = async () => {
    try {
      const client = generateClient();
      
      const CREATE_WATCHLIST_SIMPLE = `
        mutation CreateUserWatchlist($input: CreateUserWatchlistInput!) {
          createUserWatchlist(input: $input) {
            id
            userID
            name
            description
            isDefault
            isPublic
            createdAt
            updatedAt
          }
        }
      `;
      
      const result = await client.graphql({
        query: CREATE_WATCHLIST_SIMPLE,
        variables: {
          input: {
            userID: user.id,
            name: 'Want to Watch',
            description: 'Movies and shows I want to watch',
            isDefault: true,
            isPublic: false
          }
        }
      });
      
      if (result.errors && result.errors.length > 0) {
        console.error('GraphQL errors creating default watchlist:', result.errors);
        return null;
      }
      
      return result.data?.createUserWatchlist || null;
    } catch (error) {
      console.error('Error creating default watchlist:', error);
      return null;
    }
  };

  const handleViewWatchlist = useCallback((watchlist) => {
    setSelectedWatchlist(watchlist);
    setShowViewer(true);
  }, []);

  const handleEditWatchlist = useCallback((watchlist) => {
    setSelectedWatchlist(watchlist);
    setShowManager(true);
  }, []);

  const handleRefresh = useCallback(() => {
    setHasLoaded(false);
    loadWatchlists();
  }, [loadWatchlists]);



  const getWatchlistStats = () => {
    const totalItems = calculateTotalItems(watchlists);
    const publicWatchlists = watchlists.filter(w => w.isPublic).length;
    const recentlyUpdated = watchlists.filter(w => {
      const updatedDate = new Date(w.updatedAt);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return updatedDate > weekAgo;
    }).length;

    return { totalItems, publicWatchlists, recentlyUpdated };
  };

  const getWatchlistsByCategory = () => {
    const defaultWatchlists = watchlists.filter(w => w.isDefault);
    const customWatchlists = watchlists.filter(w => !w.isDefault);
    const publicWatchlists = watchlists.filter(w => w.isPublic);
    const recentWatchlists = watchlists
      .filter(w => {
        const createdDate = new Date(w.createdAt);
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return createdDate > monthAgo;
      })
      .slice(0, 3);

    return { defaultWatchlists, customWatchlists, publicWatchlists, recentWatchlists };
  };

  const WatchlistCard = ({ watchlist, showActions = true }) => {
    const itemCount = watchlist.itemCount || 0;
    const maxItems = 100; // Arbitrary max for progress bar
    const progress = Math.min((itemCount / maxItems) * 100, 100);

    return (
      <Card 
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          background: theme.palette.mode === 'dark' 
            ? 'rgba(0, 0, 0, 0.7)' 
            : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(15px)',
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 193, 7, 0.2)'
            : '1px solid rgba(25, 118, 210, 0.2)',
          '&:hover': {
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(255, 193, 7, 0.3)'
              : '0 8px 32px rgba(25, 118, 210, 0.3)',
            transform: 'translateY(-2px)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 193, 7, 0.4)'
              : '1px solid rgba(25, 118, 210, 0.4)'
          },
          transition: 'all 0.2s ease-in-out'
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h6" component="h3" noWrap sx={{ flexGrow: 1, mr: 1 }}>
              {watchlist.name}
            </Typography>

          </Box>

          <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
            {watchlist.isDefault && (
              <Chip label="Default" size="small" color="primary" />
            )}
            {watchlist.isPublic ? (
              <Chip icon={<Public />} label="Public" size="small" />
            ) : (
              <Chip icon={<Lock />} label="Private" size="small" />
            )}
          </Box>

          {watchlist.description && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 2,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {watchlist.description}
            </Typography>
          )}

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {itemCount} items
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {progress.toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>

          <Typography variant="caption" color="text.secondary">
            Updated {new Date(watchlist.updatedAt).toLocaleDateString()}
          </Typography>
        </CardContent>

        <CardActions sx={{ pt: 0, gap: 1 }}>
          <Button
            size="small"
            startIcon={<Visibility />}
            onClick={() => handleViewWatchlist(watchlist)}
            sx={{
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(45deg, #FFD700, #FFA000)'
                : 'linear-gradient(45deg, #1976d2, #42a5f5)',
              color: 'white',
              '&:hover': {
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(45deg, #FFA000, #FFD700)'
                  : 'linear-gradient(45deg, #42a5f5, #1976d2)'
              }
            }}
          >
            View
          </Button>
          <Button
            size="small"
            startIcon={<Edit />}
            onClick={() => handleEditWatchlist(watchlist)}
            variant="outlined"
            sx={{
              borderColor: theme.palette.mode === 'dark' ? '#FFD700' : '#1976d2',
              color: theme.palette.mode === 'dark' ? '#FFD700' : '#1976d2',
              '&:hover': {
                borderColor: theme.palette.mode === 'dark' ? '#FFA000' : '#42a5f5',
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 215, 0, 0.1)' 
                  : 'rgba(25, 118, 210, 0.1)'
              }
            }}
          >
            Edit
          </Button>
        </CardActions>
      </Card>
    );
  };

  const stats = getWatchlistStats();
  const categories = getWatchlistsByCategory();

  return (
    <Box sx={{ 
      p: { xs: 2, sm: 3 },
      height: { xs: '100vh', sm: 'auto' },
      overflow: { xs: 'auto', sm: 'visible' },
      pb: { xs: 10, sm: 3 } // Extra padding for mobile bottom nav
    }}>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ 
            textAlign: 'center', 
            p: 2,
            background: theme.palette.mode === 'dark' 
              ? 'rgba(0, 0, 0, 0.7)' 
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(15px)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 193, 7, 0.2)'
              : '1px solid rgba(25, 118, 210, 0.2)'
          }}>
            <Typography variant="h4" sx={{ 
              color: theme.palette.mode === 'dark' ? '#FFD700' : '#1976d2',
              fontWeight: 'bold'
            }}>
              {watchlists.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Watchlists
            </Typography>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ 
            textAlign: 'center', 
            p: 2,
            background: theme.palette.mode === 'dark' 
              ? 'rgba(0, 0, 0, 0.7)' 
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(15px)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 193, 7, 0.2)'
              : '1px solid rgba(25, 118, 210, 0.2)'
          }}>
            <Typography variant="h4" sx={{ 
              color: theme.palette.mode === 'dark' ? '#FFD700' : '#1976d2',
              fontWeight: 'bold'
            }}>
              {stats.totalItems}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Items
            </Typography>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ 
            textAlign: 'center', 
            p: 2,
            background: theme.palette.mode === 'dark' 
              ? 'rgba(0, 0, 0, 0.7)' 
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(15px)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 193, 7, 0.2)'
              : '1px solid rgba(25, 118, 210, 0.2)'
          }}>
            <Typography variant="h4" sx={{ 
              color: theme.palette.mode === 'dark' ? '#FFD700' : '#1976d2',
              fontWeight: 'bold'
            }}>
              {stats.publicWatchlists}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Public
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ 
        mb: 3,
        background: theme.palette.mode === 'dark' 
          ? 'rgba(0, 0, 0, 0.5)' 
          : 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        border: theme.palette.mode === 'dark'
          ? '1px solid rgba(255, 193, 7, 0.1)'
          : '1px solid rgba(25, 118, 210, 0.1)'
      }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              color: theme.palette.mode === 'dark' ? '#FFD700' : '#1976d2'
            }
          }}
        >
          <Tab label="All" />
          <Tab label="Default" />
          <Tab label="Custom" />
          <Tab label="Public" />
        </Tabs>
      </Box>

      {/* Cleanup Progress */}
      {cleanupInProgress && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              Cleaning up duplicate watchlists...
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button 
            size="small" 
            onClick={handleRefresh} 
            sx={{ ml: 2 }}
          >
            Retry
          </Button>
        </Alert>
      )}

      {/* Watchlist Grid */}
      {!loading && !error && (
        <Grid container spacing={2}>
          {tabValue === 0 && watchlists.map((watchlist) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={watchlist.id}>
              <WatchlistCard watchlist={watchlist} />
            </Grid>
          ))}
          
          {tabValue === 1 && categories.defaultWatchlists.map((watchlist) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={watchlist.id}>
              <WatchlistCard watchlist={watchlist} />
            </Grid>
          ))}
          
          {tabValue === 2 && categories.customWatchlists.map((watchlist) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={watchlist.id}>
              <WatchlistCard watchlist={watchlist} />
            </Grid>
          ))}
          
          {tabValue === 3 && categories.publicWatchlists.map((watchlist) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={watchlist.id}>
              <WatchlistCard watchlist={watchlist} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Empty State */}
      {watchlists.length === 0 && !loading && !error && !cleanupInProgress && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8,
          background: theme.palette.mode === 'dark' 
            ? 'rgba(0, 0, 0, 0.3)' 
            : 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 193, 7, 0.1)'
            : '1px solid rgba(25, 118, 210, 0.1)'
        }}>
          <PlaylistPlay sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No watchlists yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first watchlist to start organizing your movies and shows
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setCreateMode(true);
              setShowManager(true);
            }}
            sx={{
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(45deg, #FFD700, #FFA000)'
                : 'linear-gradient(45deg, #1976d2, #42a5f5)',
              '&:hover': {
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(45deg, #FFA000, #FFD700)'
                  : 'linear-gradient(45deg, #42a5f5, #1976d2)'
              }
            }}
          >
            Create Watchlist
          </Button>
        </Box>
      )}

      {/* FAB */}
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: { xs: 80, sm: 16 },
          right: 16,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(45deg, #FFD700, #FFA000)'
            : 'linear-gradient(45deg, #1976d2, #42a5f5)',
          '&:hover': {
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(45deg, #FFA000, #FFD700)'
              : 'linear-gradient(45deg, #42a5f5, #1976d2)'
          }
        }}
        onClick={() => {
          setCreateMode(true);
          setShowManager(true);
        }}
      >
        <Add />
      </Fab>



      {/* Dialogs */}
      {showManager && (
        <Dialog
          open={showManager}
          onClose={() => setShowManager(false)}
          maxWidth="md"
          fullWidth
          fullScreen={window.innerWidth < 600}
          PaperProps={{
            sx: {
              m: { xs: 0, sm: 2 },
              maxHeight: { xs: '100vh', sm: '90vh' },
              overflow: 'hidden'
            }
          }}
        >
          <WatchlistManager
            watchlist={selectedWatchlist}
            createMode={createMode}
            initialWatchlists={watchlists}
            onClose={() => {
              setShowManager(false);
              setSelectedWatchlist(null);
              setCreateMode(false);
              handleRefresh();
            }}
          />
        </Dialog>
      )}

      <WatchlistViewer
        watchlist={selectedWatchlist}
        onClose={() => {
          setShowViewer(false);
          setSelectedWatchlist(null);
        }}
      />
    </Box>
  );
};

export default WatchlistDashboard;